import { useState, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "./Icon"
import { getCompletedSteps, toggleStep, clearRoleProgress } from "../utils/tutorialProgress"
import { useTutorial } from "../context/TutorialContext"
import api from "../utils/api"
import { getApiErrorMessage } from "../utils/errorMessage"
import GroupSearchResultCard from "./groups/GroupSearchResultCard"
import GroupJoinRequestModal from "./groups/GroupJoinRequestModal"

// ─── Static config ────────────────────────────────────────────────────────────

const ROLE_CARDS = [
  {
    id: "tenant",
    label: "Tenant",
    description: "I rent a unit and pay rent",
    icon: "person-fill",
    color: "emerald",
  },
  {
    id: "landlord",
    label: "Landlord",
    description: "I own property that is managed",
    icon: "house-fill",
    color: "blue",
  },
  {
    id: "propertyManager",
    label: "Property Manager",
    description: "I manage properties for landlords",
    icon: "building",
    color: "purple",
  },
]

const GROUPS_CARDS = [
  {
    id: "joinGroup",
    label: "Join a Group",
    description: "I want to join an existing group",
    icon: "person-plus-fill",
    color: "sky",
  },
  {
    id: "createGroup",
    label: "Create a Group",
    description: "I want to start a new group",
    icon: "people-fill",
    color: "sky",
  },
]

// The five steps every property manager must complete to go live.
// `details` bullets are plain-English — no technical jargon.
const PM_STEPS = [
  {
    id: "createCompany",
    icon: "building-fill",
    title: "Register Your Management Company",
    summary: "This is the first thing you must do. Your company profile is your identity in the system.",
    details: [
      "You'll need: company name, phone number, office address, your position/title, and the owner's name.",
      "Once registered, your account is automatically upgraded to Property Manager and you gain access to the FRP module.",
    ],
    where: "Open the FRP module, then look for the \"Create Management Company\" option.",
    action: "Open FRP Module",
    path: "/app/frp",
  },
  {
    id: "addProperty",
    icon: "houses-fill",
    title: "Add Your First Property",
    summary: "A property is a building or complex you manage. It holds all the units inside it.",
    details: [
      "You'll need: a property name, the landlord's account (they must already be registered), and the location.",
      "If you own the property yourself, you can set yourself as the landlord.",
      "You can also set the management fee percentage and utility charges at this stage.",
    ],
    where: "In the FRP module → select your company → click \"Add Property\".",
    action: "Go to FRP Module",
    path: "/app/frp",
  },
  {
    id: "addUnits",
    icon: "door-open-fill",
    title: "Add Units to the Property",
    summary: "Units are the individual spaces tenants will rent — apartments, rooms, offices, or shops.",
    details: [
      "For each unit you need at minimum: a label or name (e.g. \"Apartment 2A\", \"Room 5\", \"Shop B\").",
      "You can also set the unit type and upload photos at this stage.",
      "You can add as many units as the property has — do them one by one.",
    ],
    where: "In the FRP module → select your property → click \"Add Unit\".",
    action: "Go to FRP Module",
    path: "/app/frp",
  },
  {
    id: "setUnitValues",
    icon: "cash-coin",
    title: "Set Rent, Deposit & Fees",
    summary: "Before you can assign a tenant, each unit needs its financial details filled in.",
    details: [
      "Set: monthly rent amount, security deposit (what the tenant pays upfront), and a late fee if applicable.",
      "A unit cannot be assigned to a tenant until the rent amount is set.",
      "You can update these values later if needed.",
    ],
    where: "In the FRP module → select your property → click on a unit → use the Edit button to update its values.",
    action: "Go to FRP Module",
    path: "/app/frp",
  },
  {
    id: "assignTenant",
    icon: "person-check-fill",
    title: "Assign a Tenant to a Unit",
    summary: "Link a tenant to their unit to start the tenancy and generate their first invoices automatically.",
    details: [
      "The tenant must have a RecordLOGS account. You can also create their account during this step if they don't have one yet.",
      "Before assigning, the tenant's wallet must have enough balance to cover the deposit plus the first month's rent.",
      "Tip: Ask your tenant to top up their wallet first via M-Pesa — Paybill 4123905, Account Number is their ID number.",
    ],
    where: "In the FRP module → select your property → click on a unit → click \"Assign Tenant\".",
    action: "Go to FRP Module",
    path: "/app/frp",
  },
]

const ACCENT = {
  emerald: { bg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200", hover: "hover:border-emerald-300 hover:bg-emerald-50" },
  blue:    { bg: "bg-blue-100",    text: "text-blue-600",    border: "border-blue-200",    hover: "hover:border-blue-300 hover:bg-blue-50" },
  purple:  { bg: "bg-purple-100",  text: "text-purple-600",  border: "border-purple-200",  hover: "hover:border-purple-300 hover:bg-purple-50" },
  sky:     { bg: "bg-sky-100",     text: "text-sky-600",     border: "border-sky-200",     hover: "hover:border-sky-300 hover:bg-sky-50" },
}

// ─── Tenant tutorial ──────────────────────────────────────────────────────────

function TenantTutorial({ onBack, onClose }) {
  return (
    <div>
      <div className="text-center py-2 mb-4">
        <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Icon name="house-heart-fill" size="1.8rem" className="text-emerald-600" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Welcome, Tenant!</h3>
        <p className="text-sm text-gray-600 leading-relaxed">
          Once a Property Manager or Admin adds you to the system and assigns you to a unit,
          you'll be able to view your invoices, payment history, and account details right here.
        </p>
      </div>

      <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-5">
        <Icon name="lightbulb-fill" size="0.9rem" className="text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800 leading-relaxed">
          Contact your property manager or landlord and ask them to add you to the system.
          They'll need your full name, ID number, and phone number.
        </p>
      </div>

      <button
        onClick={onClose}
        className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white text-sm font-bold px-4 py-3 rounded-xl hover:bg-emerald-700 transition-colors mb-3"
      >
        Got it! <Icon name="check2" size="0.9rem" />
      </button>

      <div className="text-center">
        <button onClick={onBack} className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors">
          ← Change role
        </button>
      </div>
    </div>
  )
}

// ─── Landlord tutorial ────────────────────────────────────────────────────────

function LandlordTutorial({ onBack, onSwitchToPM }) {
  return (
    <div>
      {/* Hero */}
      <div className="text-center mb-5">
        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Icon name="house-fill" size="1.8rem" className="text-blue-600" />
        </div>
        <h3 className="text-xl font-extrabold text-gray-900 mb-1">Welcome, Landlord!</h3>
        <div className="w-10 h-1 bg-green-500 rounded-full mx-auto mb-3" />
        <p className="text-sm text-gray-600 leading-relaxed">
          Your landlord profile gives you oversight of your property performance, tenant activity,
          rent collections, and financial reports.
        </p>
        <p className="text-sm text-gray-600 leading-relaxed mt-2">
          Property Managers or System Admins link properties to the appropriate landlord account
          for operational management and reporting access.
        </p>
      </div>

      {/* Property Linkage card */}
      <div className="border border-green-200 rounded-xl overflow-hidden mb-4">
        <div className="flex items-start gap-3 p-4">
          <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
            <Icon name="buildings-fill" size="1rem" className="text-green-600" />
          </div>
          <div>
            <p className="text-[13px] font-bold text-green-700 mb-1">Property Linkage</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              If your property has already been onboarded by a Property Manager, your landlord
              profile can be linked using your registered phone number or ID number.
            </p>
          </div>
        </div>
        <div className="border-t border-green-100" />
        <div className="flex items-start gap-3 p-4">
          <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center shrink-0">
            <Icon name="check-lg" size="1rem" className="text-green-600" />
          </div>
          <p className="text-xs text-gray-600 leading-relaxed pt-1">
            Once linked, property records, reports, tenants, and balances become visible under your account.
          </p>
        </div>
      </div>

      {/* PM upsell card */}
      <div className="bg-purple-50 rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 bg-purple-200 rounded-lg flex items-center justify-center shrink-0">
            <Icon name="building-fill" size="0.9rem" className="text-purple-700" />
          </div>
          <p className="text-[13px] font-extrabold text-gray-900 leading-snug">
            Do you manage your own properties?
          </p>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed mb-2">
          Many landlords directly oversee their own tenants, rent collection, maintenance, and daily operations.
        </p>
        <p className="text-xs text-gray-600 mb-3">The Property Manager setup helps you:</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4">
          {[
            "Register your management profile",
            "Assign tenants",
            "Add properties and units",
            "Monitor rent and reports",
          ].map((item) => (
            <div key={item} className="flex items-center gap-1.5">
              <Icon name="check-circle-fill" size="0.8rem" className="text-green-500 shrink-0" />
              <span className="text-[11px] text-gray-700 leading-tight">{item}</span>
            </div>
          ))}
        </div>
        <button
          onClick={onSwitchToPM}
          className="w-full flex items-center justify-between gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold px-4 py-3 rounded-xl transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon name="book-fill" size="0.9rem" />
            Start Property Manager Setup
          </div>
          <Icon name="arrow-right" size="0.9rem" />
        </button>
      </div>

      {/* Footer */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
          <Icon name="shield-check" size="0.75rem" className="text-green-500" />
          Need help? Contact your Property Manager for assistance.
        </div>
        <button onClick={onBack} className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors">
          ← Change role
        </button>
      </div>
    </div>
  )
}

// ─── PM step card ─────────────────────────────────────────────────────────────

function PMStepCard({ step, number, isDone, onToggle, onNavigate }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className={`rounded-xl border transition-colors duration-200 ${
        isDone ? "border-green-200 bg-green-50" : "border-gray-200 bg-white"
      }`}
    >
      {/* ── Header row ── */}
      <div className="flex items-start gap-3 p-3.5">

        {/* Circle toggle — marks step done/undone */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(step.id) }}
          title={isDone ? "Mark as not done" : "Mark as done"}
          className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 transition-all duration-200 ${
            isDone
              ? "border-green-500 bg-green-500 text-white shadow-sm"
              : "border-gray-300 text-gray-400 hover:border-green-400 hover:text-green-500 hover:bg-green-50"
          }`}
        >
          {isDone
            ? <Icon name="check2" size="0.65rem" />
            : <span className="text-[10px] font-black leading-none">{number}</span>
          }
        </button>

        {/* Clickable title area — expands/collapses */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-center gap-2 mb-0.5">
            <Icon
              name={step.icon}
              size="0.9rem"
              className={isDone ? "text-green-600" : "text-gray-500"}
            />
            <span className={`text-[13px] font-bold leading-tight ${isDone ? "text-green-800" : "text-gray-800"}`}>
              {step.title}
            </span>
          </div>
          <p className={`text-xs leading-relaxed ${isDone ? "text-green-700" : "text-gray-500"}`}>
            {step.summary}
          </p>
        </div>

        {/* Chevron */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-gray-400 hover:text-gray-600 transition-colors shrink-0 mt-0.5 p-0.5"
        >
          <Icon name={expanded ? "chevron-up" : "chevron-down"} size="0.8rem" />
        </button>
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div className="px-3.5 pb-3.5 border-t border-gray-100">
          <div className="ml-9 pt-3 space-y-3">

            {/* Detail bullets */}
            <ul className="space-y-1.5">
              {step.details.map((line, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-gray-300 mt-0.5 shrink-0 leading-none text-base">·</span>
                  <p className="text-xs text-gray-600 leading-relaxed">{line}</p>
                </li>
              ))}
            </ul>

            {/* Where to find it */}
            <div className="flex items-start gap-2 bg-sky-50 border border-sky-100 rounded-lg p-2.5">
              <Icon name="geo-alt-fill" size="0.75rem" className="text-sky-500 mt-0.5 shrink-0" />
              <p className="text-[11px] text-sky-700 leading-relaxed">{step.where}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => onNavigate(step.path)}
                className="flex items-center gap-1.5 bg-gray-800 hover:bg-black text-white text-xs font-bold px-3.5 py-2 rounded-lg transition-colors"
              >
                {step.action}
                <Icon name="arrow-right" size="0.75rem" />
              </button>
              {!isDone && (
                <button
                  onClick={() => onToggle(step.id)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-green-700 border border-green-200 hover:border-green-400 hover:bg-green-50 rounded-lg px-3 py-2 transition-colors"
                >
                  <Icon name="check2" size="0.75rem" />
                  Mark as done
                </button>
              )}
              {isDone && (
                <button
                  onClick={() => onToggle(step.id)}
                  className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Undo
                </button>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  )
}

// ─── Property manager tutorial ────────────────────────────────────────────────

function PMGuidedView({ onStart, onShowChecklist, onBack, onClose }) {
  return (
    <div>
      {/* Hero */}
      <div className="text-center mb-6 pt-2">
        <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Icon name="building-fill" size="1.8rem" className="text-emerald-600" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Property Manager Setup</h3>
        <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">
          Let Remi walk you through everything — from registering your company to assigning your first tenant.
          Takes about 5 minutes.
        </p>
      </div>

      {/* What you'll do */}
      <div className="space-y-2 mb-6">
        {[
          { icon: "building-fill",    label: "Register your management company" },
          { icon: "houses-fill",      label: "Add your first property" },
          { icon: "door-open-fill",   label: "Create units inside the property" },
          { icon: "person-check-fill",label: "Assign a tenant to a unit" },
        ].map(({ icon, label }) => (
          <div key={label} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3.5 py-2.5">
            <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
              <Icon name={icon} size="0.85rem" className="text-emerald-600" />
            </div>
            <span className="text-[13px] text-gray-700 font-medium">{label}</span>
          </div>
        ))}
      </div>

      {/* Primary CTA */}
      <button
        onClick={onStart}
        className="w-full flex items-center justify-between gap-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl px-4 py-3.5 mb-3 hover:from-emerald-600 hover:to-green-600 transition-all shadow-sm group"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
            <Icon name="play-fill" size="1rem" />
          </div>
          <div className="text-left">
            <div className="text-sm font-bold leading-tight">Start Guided Tutorial</div>
            <div className="text-[11px] text-emerald-100 leading-tight mt-0.5">Remi will guide you step by step</div>
          </div>
        </div>
        <Icon name="arrow-right" size="1rem" className="group-hover:translate-x-0.5 transition-transform" />
      </button>

      {/* Secondary — checklist */}
      <button
        onClick={onShowChecklist}
        className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors py-1.5"
      >
        <Icon name="list-check" size="0.75rem" />
        I prefer the checklist
      </button>

      {/* Footer nav */}
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
        <button onClick={onBack} className="text-xs font-semibold text-gray-400 hover:text-gray-700 transition-colors">
          ← Change role
        </button>
        <button onClick={onClose} className="text-xs font-semibold text-gray-400 hover:text-gray-700 transition-colors">
          Close
        </button>
      </div>
    </div>
  )
}

function PMChecklistView({ userId, onBack, onClose }) {
  const navigate = useNavigate()

  const [done, setDone] = useState(() => getCompletedSteps(userId, "propertyManager"))

  const handleToggle = useCallback((stepId) => {
    setDone(toggleStep(userId, "propertyManager", stepId))
  }, [userId])

  const handleNavigate = useCallback((path) => {
    onClose()
    navigate(path)
  }, [onClose, navigate])

  const handleReset = () => {
    clearRoleProgress(userId, "propertyManager")
    setDone([])
  }

  const doneCount = done.length
  const total = PM_STEPS.length
  const allDone = doneCount === total
  const progress = Math.round((doneCount / total) * 100)

  return (
    <div>
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-gray-700">
            {allDone ? "All steps complete!" : `${doneCount} of ${total} steps done`}
          </span>
          {doneCount > 0 && (
            <button onClick={handleReset} className="text-[10px] text-gray-400 hover:text-red-500 transition-colors">
              Reset progress
            </button>
          )}
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-green-500" : "bg-green-400"}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* All-done banner */}
      {allDone && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3.5 mb-4">
          <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
            <Icon name="trophy-fill" size="1.1rem" className="text-green-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-green-800 mb-0.5">You're all set!</p>
            <p className="text-[11px] text-green-700 leading-relaxed">
              Your system is ready for tenants. You can always come back to this guide anytime.
            </p>
          </div>
        </div>
      )}

      {!allDone && (
        <p className="text-xs text-gray-500 leading-relaxed mb-4">
          Follow these steps in order to get your first property ready. Tick each one when done —
          your progress is saved automatically.
        </p>
      )}

      {/* Steps */}
      <div className="space-y-2">
        {PM_STEPS.map((step, idx) => (
          <PMStepCard
            key={step.id}
            step={step}
            number={idx + 1}
            isDone={done.includes(step.id)}
            onToggle={handleToggle}
            onNavigate={handleNavigate}
          />
        ))}
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
        <button onClick={onBack} className="text-xs font-semibold text-gray-400 hover:text-gray-700 transition-colors">
          ← Back
        </button>
        <button onClick={onClose} className="text-xs font-semibold text-gray-400 hover:text-gray-700 transition-colors">
          Close
        </button>
      </div>
    </div>
  )
}

function PropertyManagerTutorial({ userId, onBack, onClose }) {
  const { startTutorial } = useTutorial()
  const [view, setView] = useState("guided") // "guided" | "checklist"

  if (view === "checklist") {
    return <PMChecklistView userId={userId} onBack={() => setView("guided")} onClose={onClose} />
  }

  return (
    <PMGuidedView
      onStart={() => { startTutorial("propertyManager"); onClose() }}
      onShowChecklist={() => setView("checklist")}
      onBack={onBack}
      onClose={onClose}
    />
  )
}

// ─── Create Group ────────────────────────────────────────────────────────────

const ONE_TIME_EXAMPLES = [
  { icon: "suit-heart-fill", label: "Wedding Contribution" },
  { icon: "flower1",         label: "Burial / Funeral" },
  { icon: "airplane-fill",   label: "Travel / Vacation" },
  { icon: "hospital-fill",   label: "Medical Fundraiser" },
  { icon: "three-dots",      label: "Other" },
]

const RECURRING_EXAMPLES = [
  { icon: "arrow-repeat",    label: "Chamaa (Merry-Go-Round)" },
  { icon: "piggy-bank-fill", label: "Project Savings" },
  { icon: "people-fill",     label: "Welfare (Members only)" },
]

function GroupTypeCard({ type, accentColor, icon, title, subtitle, description, examples, onStart }) {
  const colors = {
    green: {
      border:       "border-green-200",
      iconBg:       "bg-green-50",
      iconText:     "text-green-600",
      subtitleText: "text-green-600",
      examplesText: "text-green-600",
      exampleBorder:"border-green-200",
      exampleIcon:  "text-green-500",
      button:       "bg-green-600 hover:bg-green-700",
    },
    blue: {
      border:       "border-blue-200",
      iconBg:       "bg-blue-50",
      iconText:     "text-blue-600",
      subtitleText: "text-blue-600",
      examplesText: "text-blue-600",
      exampleBorder:"border-blue-200",
      exampleIcon:  "text-blue-500",
      button:       "bg-blue-600 hover:bg-blue-700",
    },
  }
  const c = colors[accentColor]

  return (
    <div className={`border ${c.border} rounded-2xl overflow-hidden mb-4`}>
      <div className="flex items-start gap-4 p-4">
        <div className={`w-16 h-16 ${c.iconBg} rounded-2xl flex items-center justify-center shrink-0`}>
          <Icon name={icon} size="2rem" className={c.iconText} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-base font-extrabold text-gray-900 mb-0.5">{title}</h4>
              <p className={`text-xs font-semibold ${c.subtitleText} mb-1.5`}>{subtitle}</p>
              <p className="text-xs text-gray-600 leading-relaxed">{description}</p>
            </div>
            <Icon name="chevron-right" size="1rem" className="text-gray-300 mt-1 shrink-0" />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 mx-4" />

      <div className="px-4 pt-3 pb-4">
        <p className={`text-[11px] font-bold ${c.examplesText} mb-2`}>Examples</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {examples.map(({ icon: exIcon, label }) => (
            <span
              key={label}
              className={`flex items-center gap-1.5 border ${c.exampleBorder} rounded-full px-2.5 py-1 text-[11px] text-gray-700 bg-white`}
            >
              <Icon name={exIcon} size="0.7rem" className={c.exampleIcon} />
              {label}
            </span>
          ))}
        </div>
        <button
          onClick={() => onStart(type)}
          className={`w-full flex items-center justify-center gap-2 ${c.button} text-white text-sm font-bold py-3 rounded-xl transition-colors`}
        >
          Start Now
          <Icon name="arrow-right" size="0.85rem" />
        </button>
      </div>
    </div>
  )
}

function CreateGroupTutorial({ onBack, onClose }) {
  const navigate = useNavigate()

  const handleStart = (category) => {
    onClose()
    navigate("/app/groups", { state: { openCreate: true, initialCategory: category } })
  }

  return (
    <div>
      {/* How it works */}
      <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-3.5 mb-4">
        <Icon name="info-circle-fill" size="1rem" className="text-green-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-[13px] font-bold text-green-700 mb-1">How it works</p>
          <p className="text-xs text-gray-600 leading-relaxed">
            Select the type of group you want to create. You'll be able to add details,
            members, and contributions in the next steps.
          </p>
        </div>
      </div>

      {/* One-Time Event */}
      <GroupTypeCard
        type="non-recurring"
        accentColor="green"
        icon="calendar2-event"
        title="One-Time Event"
        subtitle="For single or one-time events"
        description="Ideal for events that happen once and don't repeat."
        examples={ONE_TIME_EXAMPLES}
        onStart={handleStart}
      />

      {/* Recurring Group */}
      <GroupTypeCard
        type="recurring"
        accentColor="blue"
        icon="arrow-repeat"
        title="Recurring Group"
        subtitle="For ongoing or repeating contributions"
        description="Ideal for groups that contribute on a regular schedule."
        examples={RECURRING_EXAMPLES}
        onStart={handleStart}
      />

      {/* Security row */}
      <div className="flex items-center gap-3 border border-gray-200 rounded-xl p-3.5 mb-3">
        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
          <Icon name="shield-lock-fill" size="1.1rem" className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-gray-800 mb-0.5">Your data is safe</p>
          <p className="text-[11px] text-gray-500 leading-relaxed">
            We use secure encryption to protect your group information and transactions.
          </p>
        </div>
        <button className="flex items-center gap-1 border border-blue-200 text-blue-600 text-[11px] font-bold px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors shrink-0">
          <Icon name="shield-check" size="0.7rem" />
          Learn more
        </button>
      </div>

      {/* Not sure row */}
      <div className="flex items-center gap-3 border border-gray-200 rounded-xl p-3.5 mb-5">
        <div className="w-8 h-8 border-2 border-green-500 rounded-full flex items-center justify-center shrink-0">
          <Icon name="plus" size="0.9rem" className="text-green-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-green-600 mb-0.5">Not sure which to choose?</p>
          <p className="text-[11px] text-gray-500">You can always change your group type later.</p>
        </div>
        <Icon name="chevron-right" size="0.85rem" className="text-gray-300 shrink-0" />
      </div>

      <div className="text-center">
        <button onClick={onBack} className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors">
          ← Change role
        </button>
      </div>
    </div>
  )
}

// ─── Join Group ───────────────────────────────────────────────────────────────

const JOIN_PAGE_SIZE = 3

function JoinGroupTutorial({ onBack, user }) {
  const registeredName = useMemo(() => {
    const n = String(user?.name || user?.fullName || user?.username || "").trim()
    return n || "My Registered Name"
  }, [user])

  // ── Search state ───────────────────────────────────────────────────────────
  const [query, setQuery]               = useState("")
  const [results, setResults]           = useState([])
  const [visibleCount, setVisibleCount] = useState(JOIN_PAGE_SIZE)
  const [isSearching, setIsSearching]   = useState(false)
  const [searchError, setSearchError]   = useState("")
  const [searchDone, setSearchDone]     = useState(false)
  const [joinSuccess, setJoinSuccess]   = useState("")

  // ── Join modal state ───────────────────────────────────────────────────────
  const [selectedGroup, setSelectedGroup]       = useState(null)
  const [isSubmittingJoin, setIsSubmittingJoin] = useState(false)
  const [joinSubmitError, setJoinSubmitError]   = useState("")

  const searchIdRef = useRef(0)

  const handleSearch = async () => {
    const q = query.trim()
    if (!q || isSearching) return
    const requestId = ++searchIdRef.current
    setIsSearching(true)
    setSearchError("")
    setSearchDone(false)
    setResults([])
    setJoinSuccess("")
    setVisibleCount(JOIN_PAGE_SIZE)
    try {
      const res = await api.get("/groups/search", { params: { q } })
      if (searchIdRef.current !== requestId) return
      const groups = Array.isArray(res?.data?.groups) ? res.data.groups : []
      const normalizedQ = q.toLowerCase()
      setResults(groups.filter((g) => String(g?.groupCode || "").trim().toLowerCase() === normalizedQ))
      setSearchDone(true)
    } catch (err) {
      if (searchIdRef.current !== requestId) return
      setSearchError(getApiErrorMessage(err, "Failed to search groups."))
      setSearchDone(true)
    } finally {
      if (searchIdRef.current === requestId) setIsSearching(false)
    }
  }

  const handleSubmitJoinRequest = async ({ preferredNameMode, akaName }) => {
    if (!selectedGroup?.id) return
    setIsSubmittingJoin(true)
    setJoinSubmitError("")
    setJoinSuccess("")
    try {
      const res = await api.post(`/groups/${selectedGroup.id}/join-requests`, { preferredNameMode, akaName })
      if (res?.data?.joined) {
        setResults((prev) => prev.filter((r) => String(r?.id || "") !== String(selectedGroup.id)))
        setJoinSuccess(res?.data?.message || "You joined this group immediately because approval is not required.")
      } else {
        setResults((prev) =>
          prev.map((r) =>
            String(r?.id || "") === String(selectedGroup.id)
              ? {
                  ...r,
                  hasPendingJoinRequest: true,
                  hasPendingInvite: false,
                  pendingJoinRequest: {
                    id: "pending",
                    preferredDisplayName: preferredNameMode === "aka" ? String(akaName || "").trim() : registeredName,
                    preferredNameMode,
                    createdAt: new Date().toISOString(),
                  },
                }
              : r
          )
        )
        setJoinSuccess(res?.data?.message || "Join request submitted. Awaiting admin approval.")
      }
      setSelectedGroup(null)
    } catch (err) {
      setJoinSubmitError(getApiErrorMessage(err, "Failed to submit join request."))
    } finally {
      setIsSubmittingJoin(false)
    }
  }

  const noResults      = searchDone && !isSearching && !searchError && results.length === 0
  const visibleResults = results.slice(0, visibleCount)
  const hasMore        = results.length > visibleCount

  return (
    <div>
      <p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">
        Choose one of the ways below to join a group.
      </p>

      {/* ── Invited by Admin ─────────────────────────────────────────── */}
      <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mb-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center shrink-0">
            <Icon name="person-fill" size="1.4rem" className="text-green-600" />
          </div>
          <div>
            <p className="text-[15px] font-extrabold text-green-700 mb-1">Invited by Admin</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              An admin may invite you to join a group.
              The invitation will appear in your group account.
            </p>
          </div>
        </div>

        {/* Mock invitation card */}
        <div className="bg-white border border-gray-200 rounded-xl p-3.5 flex items-center gap-3">
          <div className="w-11 h-11 bg-green-100 rounded-full flex items-center justify-center shrink-0">
            <Icon name="people-fill" size="1.1rem" className="text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-gray-900 leading-tight">Group Name</p>
            <p className="text-[11px] text-gray-500 mt-0.5">Invited by: Admin Name</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className="text-xs font-bold text-red-500 border border-red-400 rounded-lg px-3 py-1.5 hover:bg-red-50 transition-colors">
              Reject
            </button>
            <button className="text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg px-3 py-1.5 transition-colors">
              Accept
            </button>
          </div>
        </div>
      </div>

      {/* ── Join with Group ID ───────────────────────────────────────── */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
            <Icon name="search" size="1.3rem" className="text-white" />
          </div>
          <div>
            <p className="text-[15px] font-extrabold text-blue-700 mb-1">Join with Group ID</p>
            <p className="text-xs text-gray-600 leading-relaxed mb-1">
              You can join a group by entering the Group ID and searching for it.
            </p>
            <p className="text-xs text-gray-600 leading-relaxed">
              If it is a closed group, the admin will review and approve your request.
            </p>
          </div>
        </div>

        {/* Search input */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-2 mb-3">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSearchDone(false); setResults([]); setJoinSuccess("") }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Enter Group ID"
            className="flex-1 text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !query.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors shrink-0"
          >
            {isSearching ? "Searching…" : "Search"}
          </button>
        </div>

        {/* Feedback */}
        {!!joinSuccess && (
          <div className="mb-3 border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs p-2.5 rounded-lg">
            {joinSuccess}
          </div>
        )}
        {!!searchError && (
          <div className="mb-3 border border-red-300 bg-red-50 text-red-700 text-xs p-2.5 rounded-lg">
            {searchError}
          </div>
        )}
        {noResults && (
          <p className="text-xs text-gray-500 mb-2">No group found with that ID.</p>
        )}

        {/* Results */}
        {visibleResults.length > 0 && (
          <div className="space-y-2">
            {visibleResults.map((group) => (
              <GroupSearchResultCard
                key={group.id}
                group={group}
                onJoin={(g) => { setJoinSubmitError(""); setSelectedGroup(g) }}
              />
            ))}
            {hasMore && (
              <button
                onClick={() => setVisibleCount((n) => n + JOIN_PAGE_SIZE)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Show more
              </button>
            )}
          </div>
        )}
      </div>

      <div className="text-center">
        <button onClick={onBack} className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors">
          ← Change role
        </button>
      </div>

      <GroupJoinRequestModal
        open={!!selectedGroup}
        group={selectedGroup}
        registeredName={registeredName}
        isSubmitting={isSubmittingJoin}
        submitError={joinSubmitError}
        onClose={() => { if (!isSubmittingJoin) { setSelectedGroup(null); setJoinSubmitError("") } }}
        onSubmit={handleSubmitJoinRequest}
      />
    </div>
  )
}

// ─── Coming-soon placeholder ──────────────────────────────────────────────────

function ComingSoon({ onBack }) {
  return (
    <div className="text-center py-10">
      <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
        <Icon name="tools" size="1.5rem" className="text-amber-500" />
      </div>
      <h3 className="text-sm font-bold text-gray-800 mb-1">Tutorial coming soon</h3>
      <p className="text-xs text-gray-500 mb-5 max-w-[16rem] mx-auto leading-relaxed">
        We're putting together a guided walkthrough for this. Check back soon!
      </p>
      <button onClick={onBack} className="text-xs font-semibold text-gray-400 hover:text-gray-700 transition-colors">
        ← Back to selection
      </button>
    </div>
  )
}

// ─── Role selection screen ────────────────────────────────────────────────────

function RoleSelection({ onSelect }) {
  return (
    <>
      <p className="text-xs text-gray-500 mb-5 leading-relaxed">
        Select what best describes you and we'll show you the right steps to get started.
      </p>

      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5">
        Are you a…
      </p>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {ROLE_CARDS.map((card) => {
          const ac = ACCENT[card.color]
          return (
            <button
              key={card.id}
              onClick={() => onSelect(card.id)}
              className={`flex flex-col items-center gap-2 p-3.5 rounded-xl border border-gray-200 ${ac.hover} transition-all text-center group`}
            >
              <div className={`w-10 h-10 rounded-full ${ac.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-150`}>
                <Icon name={card.icon} size="1.15rem" className={ac.text} />
              </div>
              <div>
                <div className="text-[12px] font-bold text-gray-800 leading-tight">{card.label}</div>
                <div className="text-[9px] text-gray-500 leading-tight mt-0.5">{card.description}</div>
              </div>
            </button>
          )
        })}
      </div>

      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5">
        Or looking to…
      </p>
      <div className="grid grid-cols-2 gap-2">
        {GROUPS_CARDS.map((card) => {
          const ac = ACCENT[card.color]
          return (
            <button
              key={card.id}
              onClick={() => onSelect(card.id)}
              className={`relative flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 ${ac.hover} transition-all group`}
            >
              {card.comingSoon && (
                <span className="absolute top-2 right-2 text-[8px] font-bold uppercase text-amber-600 bg-amber-100 rounded-full px-1.5 py-0.5 leading-none">
                  Soon
                </span>
              )}
              <div className={`w-9 h-9 rounded-full ${ac.bg} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-150`}>
                <Icon name={card.icon} size="1.05rem" className={ac.text} />
              </div>
              <div className="text-left">
                <div className="text-[12px] font-bold text-gray-800 leading-tight">{card.label}</div>
                <div className="text-[9px] text-gray-500 leading-tight mt-0.5">{card.description}</div>
              </div>
            </button>
          )
        })}
      </div>
    </>
  )
}

// ─── Modal header subtitle ────────────────────────────────────────────────────

const SUBTITLES = {
  select:          "Choose the guide that fits your role",
  tenant:          "Getting started as a Tenant",
  landlord:        "Getting started as a Landlord",
  propertyManager: "Property Manager Setup Guide",
  joinGroup:       "Choose one of the ways below to join a group",
  createGroup:     "Choose the group type that fits your event",
}
// Note: PropertyManagerTutorial manages its own internal view state (guided vs checklist)

// ─── Root modal ───────────────────────────────────────────────────────────────

function TutorialModal({ user, onClose }) {
  const [selectedId, setSelectedId] = useState(null) // null = selection screen

  const userId = user?._id ?? user?.id ?? "anonymous"

  const selectRole = (id) => setSelectedId(id)
  const goBack = () => setSelectedId(null)

  const phase = selectedId ?? "select"

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header — fixed, never scrolls */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
              <Icon name="mortarboard-fill" size="1rem" className="text-green-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900 leading-tight">Getting Started</h2>
              <p className="text-[11px] text-gray-400 leading-tight">{SUBTITLES[phase]}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
          >
            <Icon name="x-lg" size="0.95rem" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-5">

          {phase === "select" && <RoleSelection onSelect={selectRole} />}

          {phase === "tenant" && (
            <TenantTutorial onBack={goBack} onClose={onClose} />
          )}

          {phase === "landlord" && (
            <LandlordTutorial
              onBack={goBack}
              onSwitchToPM={() => selectRole("propertyManager")}
            />
          )}

          {phase === "propertyManager" && (
            <PropertyManagerTutorial
              userId={userId}
              onBack={goBack}
              onClose={onClose}
            />
          )}

          {phase === "createGroup" && (
            <CreateGroupTutorial onBack={goBack} onClose={onClose} />
          )}

          {phase === "joinGroup" && (
            <JoinGroupTutorial onBack={goBack} user={user} />
          )}

        </div>
      </div>
    </div>
  )
}

export default TutorialModal
